import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What happened today?",
  "Why is RTO increasing?",
  "Which courier performs best?",
  "Which shipments need attention?",
  "Which pincodes have high NDR?",
  "Which provider is failing?",
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function renderMd(text) {
  return text.split("\n").map((line, i) => {
    const parts = line.split(/\*\*(.+?)\*\*/g).map((p, j) => (j % 2 ? <b key={j}>{p}</b> : p));
    const isBullet = line.trim().startsWith("- ");
    return <p key={i} className={`${isBullet ? "pl-3" : ""} min-h-[0.5rem]`}>{isBullet ? "• " + parts.slice(0).map((p, j) => <span key={j}>{p}</span>) : parts}</p>;
  });
}

export default function AdminAi() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/admin/ai/history").then(({ data }) => setMessages(data.map((m) => ({ role: m.role, content: m.content })))).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ai/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch (e) {
      toast.error(fmtErr(e, "Request failed"));
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] max-w-3xl flex-col" data-testid="admin-ai-page">
      <div className="flex items-center gap-3 rounded-2xl bg-shipany-navy p-5 text-white">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-cyan text-shipany-navy"><Sparkles className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-lg font-bold">ShipAny Assistant</h1>
          <p className="text-xs text-slate-400">Ops copilot — answers from your live platform data.</p>
        </div>
      </div>

      <div className="mt-4 flex-1 space-y-4 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="mt-10 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-shipany-cyan" />
            <p className="mt-3 font-display font-bold text-shipany-navy">Ask anything about your operations</p>
            <div className="mx-auto mt-5 grid max-w-md gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => ask(s)} data-testid={`ai-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:border-shipany-blue hover:text-shipany-blue">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-shipany-blue text-white" : "bg-shipany-mist text-shipany-navy"}`}>
              {m.role === "assistant" ? renderMd(m.content || "") : m.content}
              {m.role === "assistant" && streaming && i === messages.length - 1 && <Loader2 className="inline h-3.5 w-3.5 animate-spin text-shipany-blue" />}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-4 flex gap-3">
        <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about RTO, couriers, providers, pincodes…"
          className="flex-1 rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15" />
        <button data-testid="ai-send-btn" disabled={streaming || !input.trim()}
          className="flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-3.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-50 glow-blue">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
